import axios from 'axios';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Pre-configured axios instance for BotView API.
 * Uses Authorization header for session auth (avoids CORS cookie issues with proxy).
 */
const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

// Attach session token to every request if available
api.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('botview_session_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Exchange a BotView JWT for a session token.
 * @param {string} token - JWT from the URL query param
 * @returns {Promise<{user_id:string, name:string, role:string, college_id:string, college_name:string, session_token:string}>}
 */
export async function exchangeToken(token) {
  const { data } = await api.post('/auth/session', { token });
  if (data.session_token) {
    sessionStorage.setItem('botview_session_token', data.session_token);
  }
  return data;
}

/**
 * Get the current user from the session token.
 * @returns {Promise<object|null>}
 */
export async function getMe() {
  const { data } = await api.get('/auth/me');
  return data;
}

/**
 * Logout — clears session.
 */
export async function logout() {
  try {
    await api.post('/auth/logout');
  } catch {
    // ignore
  }
  sessionStorage.removeItem('botview_session_token');
}

/**
 * Health check.
 */
export async function healthCheck() {
  const { data } = await api.get('/health');
  return data;
}

/**
 * Fetch aggregated student dashboard data.
 */
export async function getStudentProgress() {
  const { data } = await api.get('/student/progress');
  return data;
}

/**
 * Fetch Insight page analytics: dimension score averages, score-over-time
 * history, and topic breakdown.
 */
export async function getStudentInsights() {
  const { data } = await api.get('/student/insights');
  return data;
}

/**
 * Fetch the badge catalog with earned flags for the logged-in student.
 */
export async function getStudentBadges() {
  const { data } = await api.get('/student/badges');
  return data;
}

/**
 * Fetch ranked leaderboard entries for a given scope.
 * @param {'college'|'batch'|'branch'|'global'} scope
 */
export async function getLeaderboard(scope = 'college') {
  const { data } = await api.get('/leaderboard', { params: { scope } });
  return data;
}

/**
 * Fetch knowledge base resources visible to the logged-in student.
 */
export async function getKnowledgeResources() {
  const { data } = await api.get('/knowledge');
  return data;
}

/**
 * Fetch sentiment analysis + confidence score history across the student's
 * own completed sessions. Temporary student-facing endpoint — see
 * BOTVIEW_HANDOVER_V8.md for the trainer/admin migration plan.
 */
export async function getSentimentHistory() {
  const { data } = await api.get('/student/sentiment-history');
  return data;
}

export default api;
